import React from 'react';
import { createRoot } from 'react-dom/client';
import history from 'history/browser';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { switchAccount, initializeAuthClient } from 'ext/lib/auth/web';
import AccountSwitcherScreen from 'components/AccountSwitcherScreen';
import App from 'components/App';
import { Root, initialize } from 'root';
import { saveIntendedUrl, restoreIntendedUrl } from 'lib/auth-redirect';
// Public route components
import PreviewProxy from 'components/PreviewProxy';
import PreviewExperience from 'components/PreviewExperience';

if (import.meta.env.REACT_DEVTOOLS) {
  import('react-devtools');
}

// Root element to mount the builder
const $root = document.querySelector('#root');
const root = createRoot($root);

// eslint-disable-next-line unicorn/prefer-top-level-await
(async () => {
  // Check for public routes FIRST, before initializing auth
  const { pathname, hash, search } = history.location;
  const searchHash = hash.replace('#', '');
  const params = Object.fromEntries(new URLSearchParams(search || searchHash));

  // PUBLIC ROUTES - no auth needed
  // If it's a route to the image-service
  // we render an specific page without authentication
  // to render only the experience preview
  if (pathname.includes('image-service') || hash.includes('image-service')) {
    root.render(<PreviewExperience />);
    return;
  }

  // If the url has type param
  // we should render the PreviewProxy which is a non-authenticated page
  if (params?.type || params?.democues) {
    root.render(<PreviewProxy params={params} />);
    return;
  }

  // AUTHENTICATED ROUTES
  // Initialize auth client AFTER public route checks
  // This dynamically loads @appcues/auth and captures URL params for legacy auth
  await initializeAuthClient();

  /**
   * Show account switcher and wait for user selection.
   * Returns a Promise that resolves with the selected account ID.
   *
   * @param {object} accounts - Object of accounts keyed by ID
   * @return {Promise<string>} Selected account ID
   */
  const showAccountSwitcher = accounts => {
    return new Promise(resolve => {
      root.render(
        <AccountSwitcherScreen accounts={accounts} onSelect={resolve} />
      );
    });
  };

  // Save intended URL before auth redirect (in case user is not logged in)
  saveIntendedUrl();

  // Authenticate and get user initial data
  let initResult = await initialize();

  // Restore intended URL after auth (if we're at root after redirect)
  restoreIntendedUrl();

  // If user has no active account (Auth0 only), show account switcher first
  if (initResult.needsAccountSelection) {
    const selectedAccountId = await showAccountSwitcher(initResult.accounts);
    await switchAccount(selectedAccountId);
    // Re-initialize after account selection (now has active account)
    initResult = await initialize();

    // Verify re-initialization succeeded
    if (initResult.needsAccountSelection) {
      throw new Error(
        'Account selection failed. Please refresh the page and try again.'
      );
    }
  }

  const { auth, user, account, endpointOverrides, studioUrl, region } =
    initResult;

  // Initialize LaunchDarkly provider
  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.LAUNCHDARKLY_CLIENT_ID,
    context: {
      kind: 'user',
      key: auth.accountId,
      tier: account.tier,
      createdAt: account.createdAt,
      region,
    },
  });

  // Render application
  root.render(
    <LDProvider>
      <Root
        auth={auth}
        user={user}
        endpointOverrides={endpointOverrides}
        studioUrl={studioUrl}
        region={region}
      >
        <App />
      </Root>
    </LDProvider>
  );
})();
